import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import { useResourceList } from '../config/ResourcesConfigContext';
import type { ContentAudience } from '../utils/audience';

export interface FaqItemData {
    id: number;
    question: string;
    answer: string;
    category: string[];
    updatedAt: string;
    audience: ContentAudience;
}

interface FaqContextType {
    faqItems: FaqItemData[];
    isLoading: boolean;
    error: string | null;
    refresh: (force?: boolean) => Promise<void>;
    reset: () => void;
}

const FaqContext = createContext<FaqContextType | undefined>(undefined);

export function FaqProvider({ children }: { children: ReactNode }) {
    const fetchResource = useResourceList();
    const [faqItems, setFaqItems] = useState<FaqItemData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const hasFetchedRef = useRef(false);

    const fetchFaqItems = useCallback(
        async (force: boolean = false) => {
            if (!force && hasFetchedRef.current) {
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const data = await fetchResource<FaqItemData>('faqs');
                setFaqItems(data);
                hasFetchedRef.current = true;
            } catch (err) {
                const errorMessage =
                    err instanceof Error ? err.message : 'Failed to fetch FAQ items';
                setError(errorMessage);
                console.error('Failed to fetch FAQ items:', err);
            } finally {
                setIsLoading(false);
            }
        },
        [fetchResource]
    );

    const refresh = useCallback(
        async (force: boolean = false) => {
            await fetchFaqItems(force);
        },
        [fetchFaqItems]
    );
    const reset = useCallback(() => {
        hasFetchedRef.current = false;
        setFaqItems([]);
        setError(null);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchFaqItems(false);
    }, [fetchFaqItems]);

    const value: FaqContextType = { faqItems, isLoading, error, refresh, reset };

    return <FaqContext.Provider value={value}>{children}</FaqContext.Provider>;
}

export function useFaq(): FaqContextType {
    const context = useContext(FaqContext);
    if (context === undefined) {
        throw new Error('useFaq must be used within a FaqProvider');
    }
    return context;
}
